import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { CalendarIcon, User, Flag, Tag, FolderOpen, CheckCircle2, Clock, Award } from 'lucide-react';
import { format } from 'date-fns';
import { Tables } from '@/integrations/supabase/types';
import { Staff } from './SchedulerTypes';

type StaffTodo = Tables<'staff_todos'> & { category?: string; project_id?: string };
type Project = Tables<'projects'>;

interface ViewTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  todo: StaffTodo | null;
  staff: Staff[];
  projects?: Project[];
}

const ViewTaskModal: React.FC<ViewTaskModalProps> = ({
  isOpen,
  onClose,
  todo,
  staff,
  projects = [],
}) => {
  if (!todo) return null;

  const staffMember = staff.find(s => s.id === todo.staff_id);
  const project = projects.find(p => p.id === todo.project_id);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-500 bg-red-50';
      case 'high': return 'text-orange-500 bg-orange-50';
      case 'medium': return 'text-yellow-500 bg-yellow-50';
      case 'low': return 'text-green-500 bg-green-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in-progress': return 'bg-blue-500';
      case 'review': return 'bg-purple-500';
      case 'on-hold': return 'bg-orange-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-yellow-500';
    }
  };

  // Function to convert URLs in HTML to clickable links
  const processDescription = (html: string | null) => {
    if (!html) return <span className="text-muted-foreground">No description provided</span>;

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Function to process text nodes and make URLs clickable
    const processTextNode = (text: string) => {
      // Regular expression to match URLs
      const urlRegex = /(https?:\/\/[^\s<]+)/g;
      const parts = text.split(urlRegex);
      
      return parts.map((part, index) => {
        if (part.match(urlRegex)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              {part}
            </a>
          );
        }
        return part;
      });
    };

    // Process all text nodes in the HTML
    const processElement = (element: Element): React.ReactNode[] => {
      const result: React.ReactNode[] = [];
      
      element.childNodes.forEach((node, index) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          result.push(...processTextNode(text));
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          const tagName = el.tagName.toLowerCase();
          
          // Handle different HTML tags
          switch (tagName) {
            case 'a':
              result.push(
                <a
                  key={index}
                  href={el.getAttribute('href') || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  {el.textContent}
                </a>
              );
              break;
            case 'br':
              result.push(<br key={index} />);
              break;
            case 'p':
              result.push(
                <p key={index} className="mb-2 leading-relaxed">
                  {processElement(el)}
                </p>
              );
              break;
            case 'strong':
            case 'b':
              result.push(
                <strong key={index}>
                  {processElement(el)}
                </strong>
              );
              break;
            case 'em':
            case 'i':
              result.push(
                <em key={index}>
                  {processElement(el)}
                </em>
              );
              break;
            case 'ul':
              result.push(
                <ul key={index} className="list-disc ml-6 mb-2 space-y-1">
                  {processElement(el)}
                </ul>
              );
              break;
            case 'ol':
              result.push(
                <ol key={index} className="list-decimal ml-6 mb-2 space-y-1">
                  {processElement(el)}
                </ol>
              );
              break;
            case 'li':
              result.push(
                <li key={index} className="ml-2">
                  {processElement(el)}
                </li>
              );
              break;
            default:
              result.push(...processElement(el));
          }
        }
      });
      
      return result;
    };

    return <div className="space-y-2 text-sm">{processElement(tempDiv)}</div>;
  };

  // Check if task is overdue - compare dates only (ignore time)
  // Also determine if it's "critical" overdue (2+ extensions = red, otherwise orange)
  const { isOverdue, isCriticalOverdue } = (() => {
    if (!todo.deadline || todo.status === 'completed') {
      return { isOverdue: false, isCriticalOverdue: false };
    }

    const deadlineDate = new Date(todo.deadline);
    const today = new Date();
    deadlineDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const overdue = today > deadlineDate;
    // Critical overdue: 2+ extensions means red, otherwise orange
    const extensionCount = (todo as any).extension_count ?? 0;
    const critical = overdue && extensionCount >= 2;

    return { isOverdue: overdue, isCriticalOverdue: critical };
  })();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Task Details</span>
            {todo.status === 'completed' && (
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Title */}
          <div>
            <h3 className="text-lg font-semibold">{todo.title}</h3>
          </div>

          {/* Status and Priority */}
          <div className="flex gap-2">
            <Badge className={`${getStatusColor(todo.status)} text-white`}>
              {todo.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Badge>
            <Badge className={getPriorityColor(todo.priority)}>
              <Flag className="h-3 w-3 mr-1" />
              {todo.priority.charAt(0).toUpperCase() + todo.priority.slice(1)} Priority
            </Badge>
          </div>

          {/* Staff Member */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Assigned to:</span>
            <span className="text-sm">{staffMember?.name || 'Unknown'}</span>
          </div>

          {/* Start Date */}
          {todo.start_date && (
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Start Date:</span>
              <span className="text-sm text-blue-600">
                {format(new Date(todo.start_date), 'PPP')}
              </span>
            </div>
          )}

          {/* Deadline */}
          <div className="flex items-center gap-2">
            <CalendarIcon className={`h-4 w-4 ${
              isCriticalOverdue ? 'text-red-500' :
              isOverdue ? 'text-orange-500' : 'text-muted-foreground'
            }`} />
            <span className="text-sm font-medium">Deadline:</span>
            <span className={`text-sm ${
              isCriticalOverdue ? 'text-red-500 font-semibold' :
              isOverdue ? 'text-orange-500 font-semibold' : ''
            }`}>
              {todo.deadline ? format(new Date(todo.deadline), 'PPP') : 'No deadline set'}
              {isOverdue && ' (Overdue)'}
            </span>
          </div>

          {/* Estimated Duration */}
          {todo.estimated_duration && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Estimated Time:</span>
              <span className="text-sm text-blue-600">
                {(() => {
                  const hours = Math.floor(todo.estimated_duration / 60);
                  const mins = Math.round(todo.estimated_duration % 60);
                  if (hours === 0) return `${mins}m`;
                  if (mins === 0) return `${hours}h`;
                  return `${hours}h ${mins}m`;
                })()}
              </span>
            </div>
          )}

          {/* Actual Duration */}
          {todo.actual_duration && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Actual Time:</span>
              <span className="text-sm text-green-600">
                {(() => {
                  const hours = Math.floor(todo.actual_duration / 60);
                  const mins = Math.round(todo.actual_duration % 60);
                  if (hours === 0) return `${mins}m`;
                  if (mins === 0) return `${hours}h`;
                  return `${hours}h ${mins}m`;
                })()}
              </span>
            </div>
          )}

          {/* Category */}
          {todo.category && (
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Category:</span>
              <span className="text-sm">{todo.category}</span>
            </div>
          )}

          {/* Task Type */}
          {todo.task_type && (
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Task Type:</span>
              <Badge variant="outline" className={todo.task_type === 'Routine' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-orange-50 text-orange-700 border-orange-200'}>
                {todo.task_type}
              </Badge>
            </div>
          )}

          {/* Project */}
          {project && (
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Project:</span>
              <span className="text-sm">{project.name}</span>
            </div>
          )}

          {/* Completion Date */}
          {todo.completed_at && (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Completed:</span>
              <span className="text-sm">
                {format(new Date(todo.completed_at), 'PPP')}
              </span>
            </div>
          )}

          {/* Late Reason */}
          {todo.late_reason && (
            <div className="border-l-4 border-orange-400 bg-orange-50 p-3 rounded">
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-orange-900 mb-1">Late Completion Reason</div>
                  <div className="text-sm text-orange-800 whitespace-pre-wrap">{todo.late_reason}</div>
                </div>
              </div>
            </div>
          )}

          {/* Performance Review Section */}
          {((todo as any).rating || (todo as any).reviewer_comment) && (
            <div className="border-l-4 border-blue-400 bg-blue-50 p-3 rounded">
              <div className="flex items-start gap-2">
                <Award className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <div className="text-sm font-medium text-blue-900">Performance Review</div>

                  {(todo as any).rating && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-blue-800">Rating:</span>
                      <Badge variant="default" className="bg-blue-600">
                        {(todo as any).rating} / 5
                      </Badge>
                    </div>
                  )}

                  {(todo as any).reviewer_comment && (
                    <div>
                      <div className="text-sm font-medium text-blue-800 mb-1">Reviewer Feedback:</div>
                      <div className="text-sm text-blue-900 bg-white p-2 rounded border border-blue-200 whitespace-pre-wrap">
                        {(todo as any).reviewer_comment}
                      </div>
                    </div>
                  )}

                  {(todo as any).reviewed_at && (
                    <div className="text-xs text-blue-700">
                      Reviewed on {format(new Date((todo as any).reviewed_at), 'PPP')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Creation Date */}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Created:</span>
            <span className="text-sm">
              {format(new Date(todo.created_at), 'PPP')}
            </span>
          </div>

          {/* Description */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-2">Description</h4>
            <div className="bg-gray-50 rounded-lg p-4 task-description">
              {processDescription(todo.description)}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewTaskModal;